import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../../lib/api";
import { Header } from "../../components/vc/Header";
import { FeatureCard } from "../../components/vc/FeatureCard";
import { Section, EmptyState, SkeletonList } from "../../components/vc/layout";
import { OrderCard } from "../../components/vc/OrderCard";
import { EnquiryCard } from "../../components/vc/EnquiryCard";
import { TaskCard } from "../../components/vc/TaskCard";
import { FloatingButton } from "../../components/vc/FloatingButton";
import { FileText, ShoppingBag, Building2, Users, ShoppingBag as SBIcon, ClipboardList, Sparkles } from "lucide-react";

// ==== Business meta helpers ====
const orderStatusMap = { placed: "pending", in_progress: "in_progress", delivered: "delivered", cancelled: "closed" };

export default function VCDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [tasks, setTasks] = useState([]);

  useEffect(() => {
    Promise.all([
      apiClient.get("/orders").then((r) => r.data).catch(() => []),
      apiClient.get("/bulk-price-requests").then((r) => r.data).catch(() => []),
      apiClient.get("/inbound-enquiries").then((r) => r.data).catch(() => []),
      apiClient.get("/tasks?status=todo").then((r) => r.data).catch(() => []),
    ]).then(([o, outbound, inbound, t]) => {
      setOrders((o || []).slice(0, 3).map((x) => ({
        id: x.id,
        business_name: x.supplier_name || "Supplier",
        order_id_label: (x.id || "").slice(0, 6).toUpperCase(),
        date: x.created_at,
        amount: x.price,
        status: orderStatusMap[x.status] || "pending",
        items_count: x.lines?.length,
      })));
      // Merge outgoing + incoming enquiries and take the 3 most recent so the
      // Recent Enquiries widget reflects both sides of the buyer/supplier flow.
      const outMapped = (outbound || []).map((x) => {
        const items = x.items || [];
        const summary = items.slice(0, 2).map((it) => it.product || "(image)").join(", ")
          + (items.length > 2 ? `, +${items.length - 2} more` : "");
        const anyQuoted = items.some((it) => (it.quotes || []).length > 0);
        return {
          id: x.id,
          direction: "outgoing",
          business_name: Object.values(x.supplier_names || {}).slice(0, 2).join(", ") || "Suppliers",
          enquiry_id_label: (x.id || "").slice(0, 6).toUpperCase(),
          date: x.created_at,
          status: x.status === "closed" ? "closed" : (anyQuoted || x.status === "quoted") ? "responded" : "pending",
          product_summary: `${items.length} item${items.length === 1 ? "" : "s"} · ${summary}`,
          created_at: x.created_at,
        };
      });
      const inMapped = (inbound || []).map((x) => {
        const items = x.items || [];
        const summary = items.slice(0, 2).map((it) => it.product || "(image)").join(", ")
          + (items.length > 2 ? `, +${items.length - 2} more` : "");
        const anyQuoted = items.some((it) => (it.quotes || []).length > 0);
        return {
          id: x.id,
          direction: "incoming",
          business_name: x.buyer_name || "Buyer",
          enquiry_id_label: (x.id || "").slice(0, 6).toUpperCase(),
          date: x.created_at,
          status: x.status === "closed" ? "closed" : (anyQuoted || x.status === "quoted") ? "responded" : "pending",
          product_summary: `${items.length} item${items.length === 1 ? "" : "s"} · ${summary}`,
          created_at: x.created_at,
        };
      });
      const merged = [...outMapped, ...inMapped]
        .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
        .slice(0, 3);
      setEnquiries(merged);
      setTasks((t || []).slice(0, 3));
    }).finally(() => setLoading(false));
  }, []);

  return (
    <>
      <Header />
      <div className="px-4 pt-4 pb-28 space-y-6">
        {/* Greeting */}
        <div>
          <div className="flex items-center gap-1.5 text-emerald-600 text-xs font-bold uppercase tracking-wider mb-1">
            <Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} />
            <span>Namaste</span>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 leading-tight">Ready for business today?</h2>
        </div>

        {/* Feature cards 2x2 */}
        <div className="grid grid-cols-2 gap-3" data-testid="dashboard-feature-grid">
          <FeatureCard title="Raise Enquiry" subtitle="Ask suppliers for prices" Icon={FileText} tone="indigo" to="/enquiries/new" testid="feat-raise-enquiry" />
          <FeatureCard title="Place Order" subtitle="Buy from suppliers" Icon={ShoppingBag} tone="emerald" to="/orders/generate" testid="feat-place-order" />
          <FeatureCard title="Businesses" subtitle="Customers & suppliers" Icon={Building2} tone="amber" to="/businesses" testid="feat-businesses" />
          <FeatureCard title="Employees" subtitle="Assign tasks" Icon={Users} tone="violet" to="/employees" testid="feat-employees" />
        </div>

        {/* Recent orders */}
        <Section title="Recent Orders" subtitle="Latest activity" to="/orders" testid="section-recent-orders">
          {loading ? <SkeletonList /> : orders.length === 0 ? (
            <EmptyState Icon={SBIcon} title="No orders yet" subtitle="Your recent orders will appear here." />
          ) : (
            <div className="space-y-2">{orders.map((o) => <OrderCard key={o.id} order={o} to={`/orders/${o.id}`} />)}</div>
          )}
        </Section>

        {/* Recent enquiries */}
        <Section title="Recent Enquiries" subtitle="Price requests" to="/enquiries" testid="section-recent-enquiries">
          {loading ? <SkeletonList /> : enquiries.length === 0 ? (
            <EmptyState Icon={FileText} title="No enquiries yet" subtitle="Raise an enquiry to compare supplier prices." />
          ) : (
            <div className="space-y-2">{enquiries.map((e) => (
              <EnquiryCard
                key={e.id}
                enquiry={e}
                to={e.direction === "incoming" ? `/enquiries/incoming/${e.id}` : `/enquiries/${e.id}`}
              />
            ))}</div>
          )}
        </Section>

        {/* Pending tasks */}
        <Section title="Pending Tasks" subtitle="Assigned to your team" to="/tasks" testid="section-pending-tasks">
          {loading ? <SkeletonList /> : tasks.length === 0 ? (
            <EmptyState Icon={ClipboardList} title="All caught up" subtitle="No open tasks right now." />
          ) : (
            <div className="space-y-2">{tasks.map((t) => <TaskCard key={t.id} task={t} to={`/tasks`} />)}</div>
          )}
        </Section>

        {/* Promo banner */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-500 via-indigo-600 to-violet-600 p-5 text-white shadow-lg">
          <div className="absolute -right-4 -top-4 w-32 h-32 rounded-full bg-white/10" />
          <div className="absolute -right-10 bottom-0 w-24 h-24 rounded-full bg-white/10" />
          <div className="relative">
            <div className="text-[10px] font-bold uppercase tracking-widest opacity-80">Konexo Pro</div>
            <h3 className="text-lg font-bold mt-1 leading-tight">Grow your trade network</h3>
            <p className="text-xs opacity-80 mt-1 max-w-[220px]">Connect with more businesses, share QR codes, and get instant quotes.</p>
            <button
              onClick={() => navigate("/businesses")}
              data-testid="promo-cta"
              className="mt-3 h-9 px-4 rounded-full bg-white text-indigo-700 text-xs font-bold hover:bg-slate-100 transition"
            >
              Explore Businesses
            </button>
          </div>
        </div>
      </div>

      <FloatingButton to="/businesses" label="Search businesses" testid="dashboard-fab" />
    </>
  );
}
